import React, { useEffect, useState } from "react";
import axios, { AxiosRequestConfig } from "axios";

import { ArtifactListCompact } from "../ArtifactListCompact";
import { CalculationList } from "../CalculationList";
import { CharacterCard } from "../CharacterCard";
import { DamageDistrubution } from "../DamageDistrubution";
import { Spinner } from "../Spinner";
import { SubstatPriorityTable } from "../SubstatPriorityTable";
import { cssJoin } from "../../utils/helpers";
import { useParams } from "react-router-dom";

type ExpandedRowBuildsProps = {
  row: any;
  isProfile: boolean;
  invalidateCache?: () => void;
};

export const ExpandedRowBuilds: React.FC<ExpandedRowBuildsProps> = ({
  row,
  isProfile,
  invalidateCache,
}) => {
  const [isFetching, setIsFetching] = useState(true);
  const [disableAnimations, setDisableAnimations] = useState(false);
  const [iterator, setIterator] = useState(1);
  const [artifacts, setArtifacts] = useState<any[]>([]);
  const [error, setError] = useState<string>();
  const [_calculations, setCalculations] = useState<{
    calculations: any;
    chartsData: any;
  }>({
    calculations: {},
    chartsData: {},
  });

  const { calculationId, variant } = useParams();
  const calculationIdFromUrl = calculationId
    ? `${calculationId}${variant || ""}`
    : "";

  const [selectedCalculationId, setSelectedCalculationId] =
    useState<string>(calculationIdFromUrl);

  const getArtifacts = async (abortController: AbortController) => {
    if (!row.md5) return;
    const _uid = encodeURIComponent(row.uid);
    const _md5 = encodeURIComponent(row.md5);
    const artDetailsURL = `/api/artifacts/${_uid}/${_md5}`;
    const opts: AxiosRequestConfig<any> = {
      signal: abortController.signal,
    };
    const { data } = await axios.get(artDetailsURL, opts);
    setArtifacts(data.data);
  };

  const getCalculations = async (abortController: AbortController) => {
    if (!row.md5) return;
    const _uid = encodeURIComponent(row.uid);
    const _md5 = encodeURIComponent(row.md5);
    const calcDetailsURL = `/api/leaderboards/${_uid}/${_md5}`;
    const opts: AxiosRequestConfig<any> = {
      signal: abortController.signal,
      params: {
        variant: isProfile ? "profilePage" : "",
      },
    };
    const { data } = await axios.get(calcDetailsURL, opts);
    setCalculations(data.data);

    if (data.error) setError(data.error);
  };

  const getArtifactsAndCalculations = async (
    abortControllers: AbortController[]
  ) => {
    const tasks = [getCalculations, getArtifacts];
    const allPromises = tasks.map((task: any, i) => {
      return new Promise(async (resolve) => {
        try {
          await task(abortControllers[i]);
        } catch (err) {
          console.log(err);
        }
        resolve(true);
      });
    });

    setIsFetching(true);
    await Promise.all(allPromises);
    setIsFetching(false);
  };

  useEffect(() => {
    const abortController_1 = new AbortController();
    const abortController_2 = new AbortController();
    getArtifactsAndCalculations([abortController_1, abortController_2]);

    return () => {
      abortController_1.abort();
      abortController_2.abort();
    };
  }, []);

  const errorCallback = async () => {
    setDisableAnimations(true);
    setIterator((prev) => prev + 1);

    // setIsFetching(true);
    // await delay(1);
    // setIsFetching(false);
  };

  // const hasLeaderboards = Object.keys(_calculations.calculations).length > 0;

  const content = error ? (
    <span className="mt-10">{error}</span>
  ) : (
    <>
      {isProfile ? (
        <CharacterCard
          row={row}
          artifacts={artifacts}
          _calculations={_calculations}
          selectedCalculationId={selectedCalculationId}
          setSelectedCalculationId={setSelectedCalculationId}
          errorCallback={errorCallback}
          invalidateCache={invalidateCache}
        />
      ) : (
        <ArtifactListCompact row={row} artifacts={artifacts} />
      )}
      <div className="responsive-components">
        {selectedCalculationId && (
          <>
            <DamageDistrubution
              row={row}
              selectedCalculationId={selectedCalculationId}
            />
            <SubstatPriorityTable
              row={row}
              selectedCalculationId={selectedCalculationId}
            />
          </>
        )}
        <CalculationList
          row={row}
          calculations={_calculations.calculations}
          selectedCalculationId={selectedCalculationId}
        />
      </div>
    </>
  );

  return (
    <div
      key={iterator}
      className={cssJoin([
        "flex expanded-row",
        disableAnimations ? "disable-anim" : "",
      ])}
    >
      {isFetching ? <Spinner /> : content}
    </div>
  );
};
